import type { ComponentType } from "react";
import { TSeoProps } from "./seo";
import { matchRoutes, RouteObject, useRoutes } from "react-router-dom";

import type { ILazyPage } from "./lazyPage";
import { Store } from "redux";

export interface IRoute {
    component?: ComponentType<any>,
    routes?: IRoute[],
    path?: string | string[],
    renderSeo?: (store: Store, messages: { [key: string]: string }, language: string, path: string, context: any) => TSeoProps | undefined,
    loadData?: (store: Store, path?: string) => unknown,
    exact?: boolean
}

// renderSeo and loadData ride along on the route object: react-router ignores keys it
// does not know, and matchRoutes hands the whole object back, which is where the
// server reads them.
export type TAppRoute = RouteObject & Pick<IRoute, "renderSeo" | "loadData"> & {
    children?: TAppRoute[],
};

// v5 matched a path as a prefix unless the route said `exact`, and pages that render
// their own nested <Routes> depend on that. v6 always matches the whole path, so a
// non-exact leaf needs the splat back. A route with children must not get one: the
// splat would swallow the rest of the path and its children could never match.
const toPath = (path: string, exact?: boolean) =>
    exact || path.endsWith("*") ? path : `${path.replace(/\/+$/, "")}/*`;

// v6 requires a child's absolute path to start with its parent's, so children under a
// route that has a path of its own are rewritten relative to it. A child whose path
// equals the parent's is the index route.
const relativeTo = (path: string, parentPath: string) => {
    if (!parentPath || parentPath === "/") return path;
    if (path === parentPath) return "";
    if (path.startsWith(parentPath + "/")) return path.slice(parentPath.length + 1);
    return path;
};

export const routesMapper = (mappingRoutes: IRoute[], parentPath = ""): TAppRoute[] => {
    return mappingRoutes.flatMap((route): TAppRoute[] => {
        const common: TAppRoute = {
            ...(route.component && { Component: route.component }),
            ...(route.renderSeo && { renderSeo: route.renderSeo }),
            ...(route.loadData && { loadData: route.loadData }),
        };
        const paths = Array.isArray(route.path) ? route.path : route.path ? [route.path] : [];

        if (route.routes) {
            // A layout route: its own path stays whole, and it passes that path down so
            // the children can be expressed relative to it.
            const own = paths[0];
            return [{
                ...common,
                ...(own && { path: relativeTo(own, parentPath) }),
                children: routesMapper(route.routes, own || parentPath),
            }];
        }

        if (!paths.length) {
            // The pathless last entry of the old <Switch> was the catch-all.
            return [{ ...common, path: "*" }];
        }

        return paths.map(path => {
            const relative = relativeTo(path, parentPath);
            // relativeTo returns "" when the child covers the parent's own path exactly.
            if (relative === "") return { ...common, index: true } as TAppRoute;
            return { ...common, path: toPath(relative, route.exact) };
        });
    });
};

/**
 * The head the matched routes ask for, merged in the order they matched, so a page's own
 * answer wins over the layout's above it.
 *
 * Both sides work it out from the same store and the same messages and so arrive at the
 * same answer: the server to write it into the document it sends, the browser to keep it
 * right after a navigation, which the server's copy cannot do.
 */
export const seoForPath = (
    routes: TAppRoute[],
    pathname: string,
    store: Store,
    messages: { [key: string]: string },
    language: string,
    context: unknown = {},
): TSeoProps | undefined =>
    (matchRoutes(routes, pathname) ?? []).reduce<TSeoProps | undefined>((head, { route }) => {
        const asked = (route as TAppRoute).renderSeo?.(store, messages, language, pathname, context);
        return asked ? { ...head, ...asked } : head;
    }, undefined);

// useRoutes only runs inside a router, so what replaces renderRoutes is a component,
// not a function call.
export const RoutesRenderer = ({ routes }: { routes: TAppRoute[] }) => useRoutes(routes);

/**
 * Loads the page modules for the routes a path matches, and reports which modules
 * those were.
 *
 * Both sides call this before their first render: the server because renderToString
 * cannot wait for anything and it needs the module ids to look the page's assets up
 * in the build manifest, the client because hydration has to produce the markup the
 * server sent - the job loadableReady() used to do.
 */
export const preloadRoutes = async (routes: TAppRoute[], pathname: string): Promise<string[]> => {
    const pages = (matchRoutes(routes, pathname) ?? [])
        .map(({ route }) => (route as TAppRoute).Component as Partial<ILazyPage> | undefined)
        // Layout routes are imported normally and have no preload: they are already
        // in the entry bundle and need no chunk of their own.
        .filter((page): page is ILazyPage => typeof page?.preload === "function");
    await Promise.all(pages.map(page => page.preload()));
    return pages.map(page => page.moduleId);
};
